import React, { createContext, useContext, useMemo, useState } from 'react';

import { cn } from '@/lib/utils';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { SnapshotModeInfo } from './SnapshotModeInfo';
import {
  analyticsSnapshotToggleHints,
  dynamicSnapshotToggleLabels,
  sectionsHaveAnalyticsGeoSnapshot,
  showsDynamicSnapshotToggle,
  type DynamicSectionDataMode,
  type DynamicSnapshotSectionLike,
} from './dynamicSectionSnapshot';

type SitrepDynamicDataContextValue = {
  mode: DynamicSectionDataMode;
  setMode: (mode: DynamicSectionDataMode) => void;
  approvalStatus?: string | null;
  showTabs: boolean;
  hasGeoSnapshot: boolean;
};

const SitrepDynamicDataContext =
  createContext<SitrepDynamicDataContextValue | null>(null);

export function SitrepDynamicDataProvider({
  approvalStatus,
  sections,
  children,
}: {
  approvalStatus?: string | null;
  sections?: DynamicSnapshotSectionLike[] | null;
  children: React.ReactNode;
}) {
  const [mode, setMode] = useState<DynamicSectionDataMode>('live');
  const showTabs = showsDynamicSnapshotToggle(approvalStatus, sections);
  const hasGeoSnapshot = useMemo(
    () => sectionsHaveAnalyticsGeoSnapshot(sections),
    [sections]
  );

  const value = useMemo(
    () => ({ mode, setMode, approvalStatus, showTabs, hasGeoSnapshot }),
    [mode, approvalStatus, showTabs, hasGeoSnapshot]
  );

  return (
    <SitrepDynamicDataContext.Provider value={value}>
      {children}
    </SitrepDynamicDataContext.Provider>
  );
}

export function useSitrepDynamicData() {
  return useContext(SitrepDynamicDataContext);
}

export function SitrepSubmittedDataTabs({
  trailing,
  className,
  mode: modeOverride,
  onModeChange,
  hasGeoSnapshot: geoOverride,
}: {
  trailing?: React.ReactNode;
  className?: string;
  mode?: DynamicSectionDataMode;
  onModeChange?: (mode: DynamicSectionDataMode) => void;
  hasGeoSnapshot?: boolean;
} = {}) {
  const ctx = useSitrepDynamicData();
  const controlled = modeOverride != null && onModeChange != null;
  const showTabs = controlled || Boolean(ctx?.showTabs);
  const mode = modeOverride ?? ctx?.mode ?? 'live';
  const setMode = onModeChange ?? ctx?.setMode;
  const hasGeoSnapshot = geoOverride ?? ctx?.hasGeoSnapshot ?? false;

  if (!showTabs && !trailing) return null;

  const labels = ctx ? dynamicSnapshotToggleLabels(ctx.approvalStatus) : null;
  const hints = ctx
    ? analyticsSnapshotToggleHints(ctx.approvalStatus, hasGeoSnapshot)
    : null;

  return (
    <div className={cn('tw:mb-3 tw:flex tw:flex-col tw:gap-1.5', className)}>
      <div className="tw:flex tw:w-full tw:flex-wrap tw:items-center tw:justify-between tw:gap-2 tw:pr-1">
        {showTabs && ctx && setMode ? (
          <Tabs
            value={mode}
            onValueChange={(next) => {
              if (next === 'snapshot' || next === 'live') {
                setMode(next);
              }
            }}
            className="tw:min-w-0 tw:gap-0"
          >
            <TabsList aria-label="Report analytics version">
              <TabsTrigger value="snapshot">
                <SnapshotModeInfo
                  label={labels?.snapshot ?? ''}
                  hint={hints?.snapshot ?? ''}
                />
              </TabsTrigger>
              <TabsTrigger value="live">
                <SnapshotModeInfo
                  label={labels?.live ?? ''}
                  hint={hints?.live ?? ''}
                />
              </TabsTrigger>
            </TabsList>
          </Tabs>
        ) : (
          <span />
        )}
        {trailing ? <div className="tw:shrink-0">{trailing}</div> : null}
      </div>
    </div>
  );
}
